import React, { useMemo, useState } from 'react';
import { ITransacao as Transacao } from '../../../../types/firestore.types';
import { formatarMoeda } from '@/utils/currencyUtils';
import { Timestamp } from 'firebase/firestore';

interface RecentTransactionsProps {
  transacoes?: Transacao[];
  showOnlyValidating?: boolean;
  showAllStatus?: boolean;
  filterStatus?: string[]; // Novo: filtro personalizado por status
}

const formatarDataHora = (timestamp: any): string => {
  if (!timestamp) return 'N/A';

  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Data inválida';

    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return 'Erro na data';
  }
};

// ✅ CORREÇÃO: Função para obter data da transação (COMPATÍVEL COM useSaldo)
const getDataTransacao = (transacao: Transacao): Date | null => {
  if (transacao.criado_em) {
    return transacao.criado_em instanceof Timestamp 
      ? transacao.criado_em.toDate() 
      : new Date(transacao.criado_em);
  } else if (transacao.createdAt) {
    return transacao.createdAt instanceof Timestamp 
      ? transacao.createdAt.toDate() 
      : new Date(transacao.createdAt);
  } else if (transacao.data_hora) {
    return transacao.data_hora instanceof Timestamp 
      ? transacao.data_hora.toDate() 
      : new Date(transacao.data_hora);
  }
  return null;
};

// ✅ CORREÇÃO: Função para verificar se uma data é do dia atual (COMPATÍVEL COM useSaldo)
const isToday = (transacao: Transacao): boolean => {
  const dataTransacao = getDataTransacao(transacao);
  if (!dataTransacao) return false;

  const today = new Date();
  
  return (
    dataTransacao.getDate() === today.getDate() &&
    dataTransacao.getMonth() === today.getMonth() &&
    dataTransacao.getFullYear() === today.getFullYear()
  );
};

const unidades = [{ unidade_id: '1', label: 'Mr. Tugas Aeroporto' }, { unidade_id: '2', label: 'Mr. Tugas Cervejaria' }, { unidade_id: '3', label: 'Mr. Tugas Jardim Norte' }];

// CONFIGURAÇÃO COMPLETA DE STATUS - INCLUINDO expired E cancelled
const STATUS_CONFIG = {
  'pending': { label: 'Pendente', color: 'bg-yellow-500', descricao: 'Aguardando processamento' },
  'completed': { label: 'Concluído', color: 'bg-green-500', descricao: 'Transação finalizada com sucesso' },
  'failed': { label: 'Falhou', color: 'bg-red-500', descricao: 'Transação Falhou' },
  'cancelled': { label: 'Cancelado', color: 'bg-gray-500', descricao: 'Transação cancelada pelo usuário' },
  'reversed': { label: 'Estornado', color: 'bg-purple-500', descricao: 'Valor foi estornado' },
  'processing': { label: 'Processando', color: 'bg-orange-500', descricao: 'Em processamento pelo sistema' },
  'refunded': { label: 'Reembolsado', color: 'bg-indigo-500', descricao: 'Valor foi reembolsado' },
  'expired': { label: 'Expirado', color: 'bg-gray-600', descricao: 'Transação expirada' },
  'validating': { label: 'Em Validação', color: 'bg-blue-500', descricao: 'Transação em processo de validação' },
  'pendente_confirmacao': { label: 'Pendente Confirmação', color: 'bg-orange-400', descricao: 'Aguardando confirmação do caixa' }
} as const;

const getStatusBadge = (status: string) => {
  const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || {
    label: status,
    color: 'bg-gray-500',
    descricao: 'Status desconhecido'
  };

  return (
    <span 
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${config.color}`}
      title={config.descricao}
    >
      {config.label}
    </span>
  );
};

// ✅ CORREÇÃO: Função para ordenar transações por data (COMPATÍVEL COM useSaldo)
const ordenarTransacoesPorData = (transacoes: Transacao[]): Transacao[] => {
  return [...transacoes].sort((a, b) => {
    const dataA = getDataTransacao(a);
    const dataB = getDataTransacao(b);
    
    const timestampA = dataA ? dataA.getTime() : 0;
    const timestampB = dataB ? dataB.getTime() : 0;
    
    return timestampB - timestampA; // Mais recente primeiro
  });
};

// Componente para modal de detalhes (ATUALIZADO para usar getDataTransacao)
const TransactionDetailsModal: React.FC<{ 
  transacao: Transacao; 
  isOpen: boolean; 
  onClose: () => void 
}> = ({ transacao, isOpen, onClose }) => {
  if (!isOpen) return null;

  const config = STATUS_CONFIG[transacao.status as keyof typeof STATUS_CONFIG] || {
    label: transacao.status,
    color: 'bg-gray-500',
    descricao: 'Status desconhecido'
  };

  // ✅ CORREÇÃO: Usar getDataTransacao para obter a data formatada
  const dataFormatada = formatarDataHora(getDataTransacao(transacao));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">Detalhes da Transação</h3>
            <button 
              onClick={onClose}
              className="text-white bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] hover:text-text text-lg"
            >
              ✕
            </button>
          </div>
          
          <div className="space-y-4">
            <DetailItem label="Status" value={
              <div className="flex items-center gap-2">
                {getStatusBadge(transacao.status)}
                <span className="text-sm text-text/60">
                  {config.descricao}
                </span>
              </div>
            } />
            <DetailItem label="Valor" value={
              <span className={`font-bold ${transacao.tipo_operacao === 'credito' ? 'text-success' : 'text-danger'}`}>
                {formatarMoeda(transacao.valor)}
              </span>
            } />
            <DetailItem label="Tipo de Operação" value={
              <span className="capitalize bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]/10 px-2 py-1 rounded">
                {transacao.tipo_operacao}
              </span>
            } />
            <DetailItem label="Data/Hora" value={dataFormatada} />
            <DetailItem label="Unidade ID" value={unidades.find((i) => i.unidade_id === transacao.unidade_id)?.label || "Tugas Coins"} />
            
            {transacao.descricao && (
              <DetailItem label="Descrição" value={transacao.descricao} />
            )}
            
            {transacao.metodo_pagamento && (
              <DetailItem label="Método de Pagamento" value={
                <span className="capitalize">{transacao.metodo_pagamento}</span>
              } />
            )}

            {/* ✅ NOVO: Mostrar paymentIntentId se existir */}
            {transacao.paymentIntentId && (
              <DetailItem label="ID da Transação" value={
                <span className="text-xs font-mono">{transacao.paymentIntentId}</span>
              } />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between items-start py-2 border-b border-primary/20/10">
    <span className="font-medium text-text/80 min-w-[120px]">{label}:</span>
    <span className="text-text/60 text-right flex-1">{value}</span>
  </div>
);

const TransactionItem: React.FC<{ transacao: Transacao }> = ({ transacao }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  const valorSanitizado = typeof transacao.valor === 'number' ? transacao.valor : 0;
  const tipoOperacaoSanitizado = ['credito', 'debito'].includes(transacao.tipo_operacao)
    ? transacao.tipo_operacao
    : 'credito';

  // ✅ CORREÇÃO: Usar getDataTransacao para obter a data formatada
  const dataFormatada = formatarDataHora(getDataTransacao(transacao));

  return (
    <>
      <li 
        className="flex items-center justify-between p-4 bg-background-light rounded-md shadow-sm hover:shadow-md transition-all duration-200 ease-in-out border border-primary/20/10 hover:border-primary/20 cursor-pointer hover:bg-background-light/50"
        onClick={() => setShowDetails(true)}
      >
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          <div className={`w-12 h-12 flex items-center justify-center rounded-full text-lg font-semibold
            ${tipoOperacaoSanitizado === 'credito' ? 'bg-success/20 text-success' : 'bg-danger/20 text-danger'}`}
          >
            {tipoOperacaoSanitizado === 'credito' ? '↑' : '↓'}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {getStatusBadge(transacao.status)}
              {transacao.tipo === 'pix_payment' ?
              <span className="text-sm font-medium text-text truncate">
                Recarga
              </span>
              : transacao.tipo === 'cashback' ?
              <span className="text-sm font-medium text-text truncate">
                Cashback
              </span>
              : 
              <span className="text-sm font-medium text-text truncate">
                Pagamento
              </span> 
              }
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-text/60">
              <span>Unidade: {unidades.find((i) => i.unidade_id === transacao.unidade_id)?.label || "Tugas Coins"}</span>
            </div>
          </div>
        </div>
        
        <div className="text-right ml-4 min-w-[100px]">
          <p className={`font-bold text-lg ${tipoOperacaoSanitizado === 'credito' ? 'text-success' : 'text-danger'}`}>
            {tipoOperacaoSanitizado === 'credito' ? '+' : '-'}{formatarMoeda(valorSanitizado)}
          </p> 
          <p className="text-text/60 text-xs mt-1">
            {dataFormatada} 
          </p>
        </div>
      </li>  

      <TransactionDetailsModal 
        transacao={transacao}
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
      />
    </>
  ); 
};

const RecentTransactions: React.FC<RecentTransactionsProps> = ({
  transacoes = [] as any,
  showOnlyValidating = false,
  showAllStatus = true,
  filterStatus = []
}) => {
  const transacoesSeguras = Array.isArray(transacoes) ? transacoes : [];

  const transacoesProcessadas = useMemo(() => {
    try {
      // ✅ CORREÇÃO: Usar função isToday atualizada
      let transacoesDoDia = transacoesSeguras.filter(transacao => 
        isToday(transacao)
      );

      // Aplicar filtros adicionais (status)
      let transacoesFiltradas = transacoesDoDia;

      if (showOnlyValidating) {
        transacoesFiltradas = transacoesDoDia.filter(transacao => 
          transacao?.status === 'validating' || transacao?.status === 'pendente_confirmacao'
        );
      } else if (filterStatus.length > 0) {
        transacoesFiltradas = transacoesDoDia.filter(transacao => 
          transacao?.status && filterStatus.includes(transacao.status)
        );
      } else if (!showAllStatus) {
        // Para mostrar apenas pendentes e em validação (comportamento antigo)
        transacoesFiltradas = transacoesDoDia.filter(transacao => 
          transacao?.status === 'pending' || 
          transacao?.status === 'validating' || 
          transacao?.status === 'pendente_confirmacao'
        );
      }
      // Se showAllStatus=true, mostra TODOS os status (incluindo expired e cancelled)

      console.log('🔍 RecentTransactions - Filtradas:', {
        doDia: transacoesDoDia.length,
        filtradas: transacoesFiltradas.length,
        showOnlyValidating,
        showAllStatus,
        filterStatus,
        statusFinais: [...new Set(transacoesFiltradas.map(t => t.status))]
      });

      // ✅ CORREÇÃO: Usar função de ordenação atualizada
      const transacoesOrdenadas = ordenarTransacoesPorData(transacoesFiltradas);

      return showAllStatus ? transacoesOrdenadas : transacoesOrdenadas.slice(0, 5);
    } catch (error) {
      console.error('❌ Erro ao processar transações:', error);
      return [];
    }
  }, [transacoesSeguras, showOnlyValidating, showAllStatus, filterStatus]);

  // ✅ CORREÇÃO: Contadores para transações do dia usando isToday atualizada
  const contadoresStatus = useMemo(() => {
    const counts: Record<string, number> = {};

    // Inicializar contadores para todos os status conhecidos
    Object.keys(STATUS_CONFIG).forEach(status => {
      counts[status] = 0;
    });

    // Contar apenas transações do dia atual (incluindo expired e cancelled)
    transacoesSeguras.forEach(transacao => {
      if (transacao?.status && isToday(transacao)) {
        counts[transacao.status] = (counts[transacao.status] || 0) + 1;
      }
    });

    return counts;
  }, [transacoesSeguras]);

  const totalTransacoes = transacoesSeguras.length;
  const transacoesDoDiaCount = transacoesSeguras.filter(t => isToday(t)).length;
  const transacoesFiltradasCount = transacoesProcessadas.length;

  // Status que devem ser mostrados quando showAllStatus=true
  const statusAtivos = Object.entries(contadoresStatus)
    .filter(([_, count]) => count > 0)
    .map(([status]) => status);

  console.log('📈 RecentTransactions - Debug:', {
    totalTransacoes,
    transacoesDoDia: transacoesDoDiaCount,
    transacoesFiltradasCount,
    showOnlyValidating,
    showAllStatus,
    filterStatus,
    statusAtivos
  });

  return (
    <div className="bg-background p-6 rounded-lg shadow-lg border border-primary/20 hover:border-primary/20 animate-fade-in-up">
      <div className="flex justify-between items-center mb-6 border-b border-primary/20 pb-3">
        <h2 className="text-2xl font-bold text-text">
          {showOnlyValidating ? 'Pagamentos em Validação (Hoje)' : 
           filterStatus.length > 0 ? `Transações Filtradas (Hoje)` :
           showAllStatus ? 'Transações de Hoje' : 'Transações Pendentes (Hoje)'}
        </h2>
        
        <div className="flex gap-2 flex-wrap">
          <span className="bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] text-white text-sm font-medium px-3 py-1 rounded-full">
            {transacoesDoDiaCount} transações hoje
          </span>
          
          {showOnlyValidating ? (
            <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
              {contadoresStatus.validating + contadoresStatus.pendente_confirmacao} em validação
            </span>
          ) : filterStatus.length > 0 ? (
            <span className="bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full">
              {transacoesFiltradasCount} filtradas
            </span>
          ) : showAllStatus ? (
            <div className="flex gap-2 flex-wrap">
              {/* Mostrar todos os status que têm transações hoje */}
              {contadoresStatus.completed > 0 && (
                <span className="bg-green-100 text-green-800 text-sm font-medium px-2 py-1 rounded-full">
                  {contadoresStatus.completed} concluídas
                </span>
              )}
              {(contadoresStatus.validating > 0 || contadoresStatus.pendente_confirmacao > 0) && (
                <span className="bg-blue-100 text-white text-sm font-medium px-2 py-1 rounded-full">
                  {contadoresStatus.validating + contadoresStatus.pendente_confirmacao} validação
                </span>
              )}
              {contadoresStatus.pending > 0 && (
                <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-2 py-1 rounded-full">
                  {contadoresStatus.pending} pendentes
                </span>
              )}
              {contadoresStatus.expired > 0 && (
                <span className="bg-gray-100 text-gray-800 text-sm font-medium px-2 py-1 rounded-full">
                  {contadoresStatus.expired} expiradas
                </span>
              )}
              {contadoresStatus.cancelled > 0 && (
                <span className="bg-red-100 text-red-800 text-sm font-medium px-2 py-1 rounded-full">
                  {contadoresStatus.cancelled} canceladas
                </span>
              )}
              {contadoresStatus.failed > 0 && (
                <span className="bg-red-100 text-red-800 text-sm font-medium px-2 py-1 rounded-full">
                  {contadoresStatus.failed} falhas
                </span>
              )}
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-2 py-1 rounded-full">
                {contadoresStatus.pending} pendentes
              </span>
              <span className="bg-blue-100 text-white text-sm font-medium px-2 py-1 rounded-full">
                {contadoresStatus.validating + contadoresStatus.pendente_confirmacao} em validação
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Info do dia atual */}
      <div className="mb-4 p-3 bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] rounded-lg ">
        <div className="flex items-center justify-between text-sm">
          <span className="text-white font-medium">
            Mostrando transações de: <strong>{new Date().toLocaleDateString('pt-BR')}</strong>
          </span>
          <span className="bold">
            Total do dia: {transacoesDoDiaCount}
          </span>
        </div>
      </div>

      {transacoesProcessadas.length === 0 ? (
        <div className="text-center text-text/70 py-8">
          <p className="text-lg mb-2">
            {showOnlyValidating ? 'Nenhum pagamento em validação hoje' :
             filterStatus.length > 0 ? 'Nenhuma transação com os filtros aplicados hoje' :
             'Nenhuma transação encontrada para hoje'}
          </p>
          <p className="text-sm">
            Total de transações no sistema: {totalTransacoes}
            {transacoesDoDiaCount > 0 && (
              <div className="mt-2 text-left inline-block">
                <p className="font-medium">Status disponíveis hoje:</p>
                <ul className="text-xs">
                  {Object.entries(contadoresStatus)
                    .filter(([_, count]) => count > 0)
                    .map(([status, count]) => (
                      <li key={status}>- {STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.label}: {count}</li>
                    ))}
                </ul>
              </div>
            )}
          </p>
        </div>
      ) : (
        <>
          <ul className="space-y-3">
            {transacoesProcessadas.map((transacao) => (
              <TransactionItem key={transacao.id} transacao={transacao} />
            ))}
          </ul>
          
          {!showAllStatus && transacoesProcessadas.length === 5 && transacoesDoDiaCount > 5 && (
            <div className="mt-4 text-center">
              <button className="text-accent hover:text-accent/80 text-sm font-medium underline">
                Ver mais transações de hoje ({transacoesDoDiaCount - 5} restantes)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecentTransactions;